"use client";

import { useEffect } from "react";

import type {
  ResolvedApp,
  ResolvedScreen,
  StudioAppMetadataYaml,
  StudioScreenMetadataYaml,
} from "./index-types";
import { buildLocalePathname } from "./StudioScreenRenderer.utils.locale-route";
import { isPlainObject } from "./StudioScreenRenderer.utils.data";

/**
 * Client-side head sync for locale switching.
 *
 * Server-rendered metadata is baked at build time for the default locale.
 * When flows switch the interface language (persisted `state.language`) or the
 * URL carries an explicit locale prefix, the document title, description,
 * keywords, OpenGraph/Twitter text, `lang`, and `dir` must follow — otherwise
 * the body shows translated copy while the tab title and social metas stay in
 * the previous language.
 *
 * Every value is resolved through a locale-aware lookup order:
 *
 *   1. `app.screenMetadataByLocale[language][slug]` — screen-localized copy
 *   2. `app.metadataByLocale[language]` — app-level localized copy
 *   3. `app.metadata` — structural/default-locale fallback
 *   4. Locale bundle namespaces (`app.meta.*` / `<slug>.meta.*`) — hosts that
 *      only ship locale bundles
 *
 * The loader pre-merges the default locale into each non-default bundle, so
 * once a locale resolves, all keys are present; no per-key English fallback
 * chain is needed here.
 *
 * For SEO, each locale-prefixed URL also gets a self-referencing canonical
 * link, per-locale `hreflang` alternates, and an `x-default` pointing at the
 * default locale's equivalent page.
 */

function readNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function readStringList(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const entries = value
    .map((entry) => readNonEmptyString(entry))
    .filter((entry): entry is string => Boolean(entry));
  return entries.length > 0 ? entries : null;
}

function mergeRecords(
  base: Record<string, unknown>,
  override: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(override)) {
    const previous = out[key];
    out[key] =
      isPlainObject(previous) && isPlainObject(value)
        ? mergeRecords(previous, value)
        : value;
  }
  return out;
}

/** Read a dotted path (e.g. "app.meta.title") inside a locale bundle. */
function readBundlePath(
  bundle: Record<string, unknown> | undefined,
  path: string,
): unknown {
  if (!bundle) return undefined;
  let current: unknown = bundle;
  for (const segment of path.split(".").filter(Boolean)) {
    if (!isPlainObject(current)) return undefined;
    current = current[segment];
  }
  return current;
}

/**
 * Read localized screen metadata from the bundle fallback shape:
 * `bundle.<key>` (its `meta` sub-map, or plain title/description).
 */
function readScreenMetaFromBundle(
  bundle: Record<string, unknown> | undefined,
  key: string,
): StudioScreenMetadataYaml | undefined {
  const source = readBundlePath(bundle, key);
  if (!isPlainObject(source)) return undefined;

  const metaObject = isPlainObject(source.meta) ? source.meta : null;
  const producer = metaObject ?? source;

  const out: Record<string, unknown> = {};
  const title = readNonEmptyString(metaObject ? producer.title : source.title);
  const description = readNonEmptyString(
    metaObject ? producer.description : source.description,
  );
  const keywords = readStringList(producer.keywords);
  if (title) out.title = title;
  if (description) out.description = description;
  if (keywords) out.keywords = keywords;
  if (typeof producer.noIndex === "boolean") out.noIndex = producer.noIndex;

  return Object.keys(out).length > 0
    ? (out as StudioScreenMetadataYaml)
    : undefined;
}

/**
 * Candidate bundle/metadata keys for a screen.
 *
 * Mini-app locale bundles register their metadata under the mini-app's own
 * namespace, which does not always equal the route slug: the frontend app
 * ships `features.yaml` (namespace `features`) while the route slug is
 * `frontend-features`. A strict slug-only lookup missed those entries, so the
 * document title fell back to the default-locale manifest title even though
 * the localized copy existed in the bundle. Candidates are ordered most
 * specific first: exact slug, manifest id, then progressively prefix-stripped
 * variants (`frontend-features` -> `features`) and the trailing segment.
 */
function getScreenMetaCandidates(
  slug: string,
  screenId: string | undefined,
): string[] {
  const out: string[] = [];
  const push = (value: string | undefined | null): void => {
    const trimmed = typeof value === "string" ? value.trim() : "";
    if (trimmed && !out.includes(trimmed)) out.push(trimmed);
  };

  push(slug);
  push(screenId);

  const parts = slug.split("-").filter(Boolean);
  for (let index = 1; index < parts.length; index += 1) {
    push(parts.slice(index).join("-"));
  }
  push(parts[parts.length - 1]);

  return out;
}

/**
 * Read localized app metadata from the bundle fallback shape:
 * `bundle.app.meta` (or `bundle.app.metadata`).
 */
function readAppMetaFromBundle(
  bundle: Record<string, unknown> | undefined,
): StudioAppMetadataYaml | undefined {
  const appNamespace = readBundlePath(bundle, "app");
  if (!isPlainObject(appNamespace)) return undefined;
  const source = isPlainObject(appNamespace.meta)
    ? appNamespace.meta
    : isPlainObject(appNamespace.metadata)
      ? appNamespace.metadata
      : null;
  if (!source) return undefined;

  const out: Record<string, unknown> = {};
  for (const key of [
    "title",
    "titleTemplate",
    "description",
    "applicationName",
    "category",
  ]) {
    const value = readNonEmptyString(source[key]);
    if (value) out[key] = value;
  }
  const keywords = readStringList(source.keywords);
  if (keywords) out.keywords = keywords;

  return Object.keys(out).length > 0
    ? (out as StudioAppMetadataYaml)
    : undefined;
}

function resolveLocaleBundle(
  app: ResolvedApp,
  language: string | undefined,
): Record<string, unknown> | undefined {
  const active =
    language?.trim() || app.i18n?.activeLocale || app.i18n?.defaultLocale;
  if (!active) return app.translations;
  return app.translationsByLocale?.[active] ?? app.translations;
}

interface ResolvedHeadMetadata {
  title: string;
  titleTemplate?: string;
  description?: string;
  applicationName?: string;
  keywords?: string[];
  openGraphLocale?: string;
  openGraphTitle?: string;
  openGraphDescription?: string;
  openGraphSiteName?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterSite?: string;
  twitterCreator?: string;
  dir?: string;
}

function applyTitleTemplate(
  template: string | null,
  title: string,
  fallbackTitle: string,
): string {
  if (!template) return title || fallbackTitle;
  if (template.includes("%s")) {
    return template.replace("%s", title || fallbackTitle);
  }
  return template;
}

function resolveHeadMetadata(options: {
  app: ResolvedApp;
  screen: ResolvedScreen | null;
  language: string | undefined;
}): ResolvedHeadMetadata {
  const { app, screen, language } = options;
  const activeLocale =
    language?.trim() || app.i18n?.activeLocale || app.i18n?.defaultLocale || "en";
  const bundle = resolveLocaleBundle(app, activeLocale);
  const slug = screen?.manifest.slug ?? "";

  // 1. Screen-level localized metadata, with the same merge order the server
  //    uses: per-locale screen metadata wins over the default-locale one.
  //
  //    Lookups run over the candidate namespace keys so mini-app backed
  //    screens (slug `frontend-features`, bundle namespace `features`) still
  //    find their localized copy. Candidates are merged from least to most
  //    specific, so the exact slug lookup always wins over an alias.
  let screenMeta: Record<string, unknown> = {};
  const defaultScreenMeta = slug ? app.screenMetadata?.[slug] : undefined;
  const localizedScreenMeta = slug
    ? app.screenMetadataByLocale?.[activeLocale]?.[slug]
    : undefined;
  if (isPlainObject(defaultScreenMeta)) screenMeta = { ...defaultScreenMeta };
  if (isPlainObject(localizedScreenMeta)) {
    screenMeta = mergeRecords(screenMeta, localizedScreenMeta);
  }

  const candidates = getScreenMetaCandidates(slug, screen?.manifest.id);
  for (let index = candidates.length - 1; index >= 0; index -= 1) {
    const key = candidates[index] as string;
    const fromMetadataMap =
      app.screenMetadataByLocale?.[activeLocale]?.[key];
    if (isPlainObject(fromMetadataMap)) {
      screenMeta = mergeRecords(screenMeta, fromMetadataMap);
    }
    const fromBundle = readScreenMetaFromBundle(bundle, key);
    if (isPlainObject(fromBundle)) {
      screenMeta = mergeRecords(screenMeta, fromBundle);
    }
  }

  // 2. App-level metadata: structural default under the localized variant.
  let appMeta: Record<string, unknown> = {};
  const baseMeta = app.metadata;
  const localizedAppMeta = app.metadataByLocale?.[activeLocale];
  const bundleAppMeta = readAppMetaFromBundle(bundle);
  if (isPlainObject(baseMeta)) appMeta = { ...baseMeta };
  if (isPlainObject(bundleAppMeta)) appMeta = mergeRecords(appMeta, bundleAppMeta);
  if (isPlainObject(localizedAppMeta)) appMeta = mergeRecords(appMeta, localizedAppMeta);

  // App-level values are the fallback when the screen has no localized title.
  const fallbackTitle =
    readNonEmptyString(screenMeta.title) ??
    readNonEmptyString(screen?.manifest.title) ??
    readNonEmptyString(appMeta.title) ??
    "";
  const fallbackTemplate =
    readNonEmptyString(appMeta.titleTemplate) ??
    readNonEmptyString(screenMeta.titleTemplate) ??
    null;

  const screenDescription = readNonEmptyString(screenMeta.description);
  const appDescription = readNonEmptyString(appMeta.description);
  const openGraph = isPlainObject(appMeta.openGraph) ? appMeta.openGraph : {};
  const twitter = isPlainObject(appMeta.twitter) ? appMeta.twitter : {};

  return {
    title: applyTitleTemplate(fallbackTemplate, fallbackTitle, fallbackTitle),
    titleTemplate: fallbackTemplate ?? undefined,
    description: screenDescription ?? appDescription ?? undefined,
    applicationName: readNonEmptyString(appMeta.applicationName) ?? undefined,
    keywords:
      readStringList(screenMeta.keywords) ?? readStringList(appMeta.keywords) ?? undefined,
    openGraphLocale: readNonEmptyString(openGraph.locale) ?? undefined,
    openGraphTitle:
      (readNonEmptyString(openGraph.title) ?? fallbackTitle) || undefined,
    openGraphDescription: screenDescription ?? appDescription ?? undefined,
    openGraphSiteName: readNonEmptyString(openGraph.siteName) ??
      readNonEmptyString(appMeta.applicationName) ?? undefined,
    twitterCard: readNonEmptyString(twitter.card) ?? undefined,
    twitterTitle:
      (readNonEmptyString(twitter.title) ?? fallbackTitle) || undefined,
    twitterDescription: screenDescription ?? appDescription ?? undefined,
    twitterSite: readNonEmptyString(twitter.site) ?? undefined,
    twitterCreator: readNonEmptyString(twitter.creator) ?? undefined,
    dir: activeLocale ? app.i18n?.dir?.[activeLocale] : undefined,
  };
}

function upsertMetaAttributeValue(
  attr: "name" | "property",
  attrValue: string,
  content: string,
): void {
  if (!content.trim()) return;
  let element = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${attrValue.replace(/"/g, '\\"')}"]`,
  );
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attr, attrValue);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function upsertMetaName(name: string, content: string): void {
  upsertMetaAttributeValue("name", name, content);
}

function upsertMetaProperty(property: string, content: string): void {
  upsertMetaAttributeValue("property", property, content);
}

function upsertLink(
  rel: string,
  href: string,
  attributes?: Record<string, string>,
): void {
  if (!href.trim()) return;
  const attrEntries = Object.entries(attributes ?? {}).filter(
    ([, value]) => typeof value === "string" && value.trim(),
  );
  const selector = `link[rel="${rel}"]${attrEntries
    .map(([key, value]) => `[${key}="${value.replace(/"/g, '\\"')}"]`)
    .join("")}`;
  let element = document.head.querySelector<HTMLLinkElement>(selector);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    for (const [key, value] of attrEntries) element.setAttribute(key, value);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

export interface DocumentMetadataSyncOptions {
  app: ResolvedApp;
  screen: ResolvedScreen | null;
  language: string | undefined;
}

/**
 * Keep the document head in sync with the active locale and screen.
 *
 * Runs as a passive effect (head writes are not paint-critical) and is keyed
 * on the resolved metadata snapshot so switching `state.language` or the URL
 * locale re-runs it with the localized title/description/OG/Twitter values.
 * When the app declares locales, the canonical URL and reciprocal hreflang
 * alternates (plus x-default) are written for the locale-prefixed URLs.
 */
export function useDocumentMetadataSync(
  options: DocumentMetadataSyncOptions,
): void {
  const { app, screen, language } = options;

  useEffect(() => {
    if (typeof document === "undefined") return;

    const meta = resolveHeadMetadata({ app, screen, language });
    const activeLocale =
      language?.trim() || app.i18n?.activeLocale || app.i18n?.defaultLocale;
    const locales = app.i18n?.locales ?? [];
    const defaultLocale = app.i18n?.defaultLocale ?? locales[0] ?? "en";

    document.title = meta.title || document.title;

    if (meta.description) upsertMetaName("description", meta.description);
    if (meta.keywords && meta.keywords.length > 0) {
      upsertMetaName("keywords", meta.keywords.join(", "));
    }

    if (meta.openGraphTitle) upsertMetaProperty("og:title", meta.openGraphTitle);
    if (meta.openGraphDescription) {
      upsertMetaProperty("og:description", meta.openGraphDescription);
    }
    if (meta.openGraphSiteName) {
      upsertMetaProperty("og:site_name", meta.openGraphSiteName);
    }
    if (meta.openGraphLocale) {
      upsertMetaProperty("og:locale", meta.openGraphLocale);
    }

    if (meta.twitterCard) upsertMetaName("twitter:card", meta.twitterCard);
    if (meta.twitterTitle) upsertMetaName("twitter:title", meta.twitterTitle);
    if (meta.twitterDescription) {
      upsertMetaName("twitter:description", meta.twitterDescription);
    }
    if (meta.twitterSite) upsertMetaName("twitter:site", meta.twitterSite);
    if (meta.twitterCreator) {
      upsertMetaName("twitter:creator", meta.twitterCreator);
    }

    if (activeLocale && locales.length > 0 && typeof window !== "undefined") {
      // Every locale-prefixed URL is self-canonical and cross-links to its
      // per-locale equivalents so search engines can consolidate them.
      const origin = window.location.origin;
      const pathname = window.location.pathname;
      upsertLink(
        "canonical",
        `${origin}${buildLocalePathname(pathname, activeLocale, locales)}`,
      );
      for (const locale of locales) {
        upsertLink(
          "alternate",
          `${origin}${buildLocalePathname(pathname, locale, locales)}`,
          { hreflang: locale },
        );
      }
      if (locales.includes(defaultLocale)) {
        upsertLink(
          "alternate",
          `${origin}${buildLocalePathname(pathname, defaultLocale, locales)}`,
          { hreflang: "x-default" },
        );
      }
    } else if (activeLocale) {
      upsertLink("alternate", `?lang=${encodeURIComponent(activeLocale)}`);
    }

    const htmlElement = document.documentElement;
    if (activeLocale) htmlElement.setAttribute("lang", activeLocale);
    if (meta.dir) htmlElement.setAttribute("dir", meta.dir);
  }, [app, screen, language]);
}